import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams, useLocation, UNSAFE_LocationContext } from 'react-router-dom';
import axios from 'axios';
import { storefrontPrefillId, storefrontStorageSuffix } from '../memory-lite/storefront-prefill';
import { secureUuid } from '../shared/utils/secure-uuid';
import { authContext } from '@store/index';
import type { GameKey } from '@shared/types';

interface ConstructorByTitleProps {
  constructorComponents: Record<string, React.ComponentType<any>>;
}

// Латинский слаг товара MemoryLite в адресе; ключи черновика и gameName остаются на названии игры.
const PRODUCT_SLUG = /^memory-[a-z0-9]+(-[a-z0-9]+)*$/;

export const ConstructorByTitle: React.FC<ConstructorByTitleProps> = (props) => {
  const { titleOrType: routeName } = useParams<{ titleOrType: string }>();
  const [canonicalName, setCanonicalName] = useState<string | null>(
    routeName && PRODUCT_SLUG.test(routeName) ? null : routeName ?? ''
  );

  useEffect(() => {
    if (!routeName || !PRODUCT_SLUG.test(routeName)) {
      setCanonicalName(routeName ?? '');
      return;
    }
    let cancelled = false;
    axios
      .get(`/public/games/${encodeURIComponent(routeName)}?t=${Date.now()}`)
      .then((res) => { if (!cancelled) setCanonicalName(res.data?.title || routeName); })
      .catch(() => { if (!cancelled) setCanonicalName(routeName); });
    return () => { cancelled = true; };
  }, [routeName]);

  if (canonicalName === null) return <div style={{ padding: 40, textAlign: 'center', fontFamily: 'sans-serif', color: '#666' }}>Loading...</div>;
  return <SlugArtAsTitle routeName={routeName} title={canonicalName}><ConstructorByTitleInner {...props} titleOrType={canonicalName} /></SlugArtAsTitle>;
};

/**
 * В адресе `?art=` тоже латинский слаг, а черновики, ключи и articleId живут на названии игры.
 * Подменяем art на название только для вложенного конструктора; адресная строка не меняется.
 */
const SlugArtAsTitle: React.FC<{ routeName?: string; title: string; children: React.ReactNode }> = ({ routeName, title, children }) => {
  const location = useLocation();
  const parent = useContext(UNSAFE_LocationContext);
  const value = useMemo(() => {
    const params = new URLSearchParams(location.search);
    if (!routeName || title === routeName || params.get('art') !== routeName) return parent;
    params.set('art', title);
    return { ...parent, location: { ...location, search: `?${params.toString()}` } };
  }, [location, parent, routeName, title]);
  return <UNSAFE_LocationContext.Provider value={value}>{children}</UNSAFE_LocationContext.Provider>;
};

const ConstructorByTitleInner: React.FC<ConstructorByTitleProps & { titleOrType: string }> = ({ constructorComponents, titleOrType }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [authData] = useContext(authContext);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ConstructorComponent, setConstructorComponent] = useState<React.ComponentType<any> | null>(null);
  const [resolvedGame, setResolvedGame] = useState<{ title: string; gameType: string } | null>(null);
  // Добавляем состояние для отслеживания статуса "создано", если оно обновилось во время запроса
  const [actuallyCreated, setActuallyCreated] = useState<boolean | null>(null);

  // --- Генерация резервных ключей (ВАЖНО: перед любыми условными return!) ---
  const art = searchParams.get('art');
  const price = searchParams.get('price');
  const storefrontPrefill = storefrontPrefillId(searchParams);
  const storefrontSuffix = storefrontStorageSuffix(searchParams);
  const urlKey = searchParams.get('key');

  const [syncedIsPaid, setSyncedIsPaid] = useState<boolean | null>(null);
  const [resolvedBuildId, setResolvedBuildId] = useState<string>('');
  // При несовпадении товара с ?key= — подменяем ключ (нельзя платить подпиской Memory за Miner)
  const [replacedReservedKey, setReplacedReservedKey] = useState<string>('');
  const [keyResolveDone, setKeyResolveDone] = useState(() => !searchParams.get('key'));

  // Генерируем резервные ключи на верхнем уровне компонента
  const { reservedKey: memoReservedKey, reservedBuildId: initialBuildId, isCreated, storedIsPaid } = useMemo(() => {
    if (!art) {
      return { reservedKey: '', reservedBuildId: '', isCreated: false, storedIsPaid: false as boolean | undefined };
    }

    // FIX: Используем имя игры для уникальности ключа (как в конструкторах)
    const safeGameName = (titleOrType || 'Memory').replace(/[^a-zA-Z0-9]/g, '_');
    const storageKey = `temp_paywall_${art}_${safeGameName}${storefrontSuffix}`;
    const stored = localStorage.getItem(storageKey);

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        // При ключе из URL всегда предпочитаем серверный buildId (не клиентский UUID),
        // кроме случая когда уже заменили mismatched subscription key
        const useUrlKey = urlKey && !parsed.replacedMismatchedKey;
        return {
          reservedKey: useUrlKey ? urlKey : (parsed.key || urlKey || ''),
          reservedBuildId: parsed.buildId || '',
          isCreated: parsed.isCreated || false,
          storedIsPaid: parsed.isPaid === true ? true : undefined,
        };
      } catch {
        // Если не удалось распарсить, генерируем новые
      }
    }

    // Ключ из URL (подписка / login redirect) — НЕ генерируем новый buildId,
    // берём канонический gameBuildId с сервера (см. useEffect ниже)
    if (urlKey) {
      localStorage.setItem(storageKey, JSON.stringify({
        key: urlKey,
        buildId: '',
        isCreated: false,
        title: titleOrType,
        awaitingServerBuildId: true,
      }));
      return {
        reservedKey: urlKey,
        reservedBuildId: '',
        isCreated: false,
        storedIsPaid: undefined,
      };
    }

    // Генерируем новые ключи
    const newKey = secureUuid().replace(/-/g, '').substring(0, 32);
    const newBuildId = secureUuid();

    // Сохраняем для следующих загрузок
    localStorage.setItem(storageKey, JSON.stringify({
      key: newKey,
      buildId: newBuildId,
      isCreated: false,
      title: titleOrType // Сохраняем оригинальное название
    }));

    return {
      reservedKey: newKey,
      reservedBuildId: newBuildId,
      isCreated: false,
      storedIsPaid: undefined,
    };
  }, [art, titleOrType, urlKey, storefrontSuffix]);

  const reservedKey = replacedReservedKey || memoReservedKey;
  const reservedBuildId = resolvedBuildId || initialBuildId;

  // Резолв канонического gameBuildId по ключу подписки — до оплаты/сейва
  useEffect(() => {
    if (!urlKey || !art) {
      setKeyResolveDone(true);
      return;
    }

    const safeGameName = (titleOrType || 'Memory').replace(/[^a-zA-Z0-9]/g, '_');
    const storageKey = `temp_paywall_${art}_${safeGameName}${storefrontSuffix}`;

    let cancelled = false;
    (async () => {
      try {
        const { getBaseUrl } = await import('@services/api');
        const baseUrl = getBaseUrl();
        const response = await fetch(`${baseUrl}/game-key/by-key/${encodeURIComponent(urlKey)}`, {
          credentials: 'include',
        });
        if (cancelled) return;
        if (!response.ok) {
          // Нет ключа на сервере — стартуем freemium с новым buildId
          const newBuildId = secureUuid();
          setResolvedBuildId(newBuildId);
          localStorage.setItem(storageKey, JSON.stringify({
            key: urlKey,
            buildId: newBuildId,
            isCreated: false,
            title: titleOrType,
            awaitingServerBuildId: false,
          }));
          return;
        }
        const data = await response.json();
        const serverBuildId = data?.gameKey?.gameBuildId as string | undefined;

        // Previously issued storefront links selected only the constructor type.
        // These purchased keys carry the authoritative design name, without art.
        if (data?.gameKey?.isPaid === true && !data.gameKey.articleId && data.gameKey.gameName && data.gameKey.gameName !== 'MasterCode') {
          navigate(`/login#${new URLSearchParams({ code: urlKey }).toString()}`, { replace: true });
          return;
        }

        const keyArticleId = data?.gameKey?.articleId != null ? String(data.gameKey.articleId) : '';
        // Ключ подписки/другого товара нельзя использовать для оплаты чужого SKU
        if (keyArticleId && keyArticleId !== String(art)) {
          console.warn('❌ Key belongs to different product, starting fresh freemium identity', {
            keyArticleId,
            art,
          });
          const newKey = secureUuid().replace(/-/g, '').substring(0, 32);
          const newBuildId = secureUuid();
          setReplacedReservedKey(newKey);
          setResolvedBuildId(newBuildId);
          localStorage.setItem(storageKey, JSON.stringify({
            key: newKey,
            buildId: newBuildId,
            isCreated: false,
            isPaid: false,
            title: titleOrType,
            awaitingServerBuildId: false,
            replacedMismatchedKey: true,
          }));
          return;
        }

        if (!serverBuildId) {
          const newBuildId = secureUuid();
          setResolvedBuildId(newBuildId);
          localStorage.setItem(storageKey, JSON.stringify({
            key: urlKey,
            buildId: newBuildId,
            isCreated: false,
            title: titleOrType,
            awaitingServerBuildId: false,
          }));
          return;
        }

        setResolvedBuildId(serverBuildId);
        if (data.gameKey.isPaid === true) setSyncedIsPaid(true);
        else if (data.gameKey.isPaid === false) setSyncedIsPaid(false);
        if (data.gameKey.isCreated) setActuallyCreated(true);

        const stored = localStorage.getItem(storageKey);
        const parsed = stored ? JSON.parse(stored) : {};
        localStorage.setItem(storageKey, JSON.stringify({
          ...parsed,
          key: urlKey,
          buildId: serverBuildId,
          isCreated: data.gameKey.isCreated || parsed.isCreated || false,
          isPaid: data.gameKey.isPaid,
          awaitingServerBuildId: false,
          title: titleOrType,
        }));
      } catch (e) {
        console.error('❌ Failed to resolve buildId by key:', e);
      } finally {
        if (!cancelled) setKeyResolveDone(true);
      }
    })();

    return () => { cancelled = true; };
  }, [urlKey, art, titleOrType, navigate]);

  useEffect(() => {
    if (initialBuildId && !resolvedBuildId) {
      setResolvedBuildId(initialBuildId);
    }
  }, [initialBuildId, resolvedBuildId]);

  useEffect(() => {
    const artParam = searchParams.get('art');

    if (artParam && !authData) {
      const safeGameName = (resolvedGame?.title || titleOrType || 'Memory').replace(/[^a-zA-Z0-9]/g, '_');
      const storageKey = `temp_paywall_${artParam}_${safeGameName}${storefrontSuffix}`;
      const stored = localStorage.getItem(storageKey);

      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          const { buildId, key, isCreated: storedIsCreated } = parsed;

          // Если у нас есть buildId, мы ДОЛЖНЫ проверить его в БД, 
          // даже если в localStorage написано isCreated: false (могло устареть)
          if (buildId && key) {
            const fetchPaywallGameData = async () => {
              try {
                const { getBaseUrl } = await import('@services/api');
                const baseUrl = getBaseUrl();
                const response = await fetch(`${baseUrl}/game-key/by-build/${buildId}`, {
                  credentials: 'include',
                  headers: {
                    'x-preview-key': key,
                  },
                });

                if (response.ok) {
                  const data = await response.json();
                  if (data.gameKey) {
                    const updates: Record<string, unknown> = { ...parsed };

                    if (!storedIsCreated) {
                      updates.isCreated = true;
                      setActuallyCreated(true);
                    }

                    if (data.gameKey.isPaid === true) {
                      updates.isPaid = true;
                      setSyncedIsPaid(true);
                    } else if (data.gameKey.isPaid === false) {
                      updates.isPaid = false;
                      setSyncedIsPaid(false);
                    }

                    if (data.gameKey.gameBuildId && data.gameKey.gameBuildId !== buildId) {
                      updates.buildId = data.gameKey.gameBuildId;
                      setResolvedBuildId(data.gameKey.gameBuildId);
                    }

                    localStorage.setItem(storageKey, JSON.stringify(updates));
                  }
                }
              } catch (error) {
                console.error('❌ Error verifying paywall game:', error);
              }
            };

            fetchPaywallGameData();
          }
        } catch (e) {
          console.error('Failed to parse paywall storage:', e);
        }
      }
    }
  }, [resolvedGame, searchParams, authData, titleOrType]);

  useEffect(() => {
    const loadConstructor = async () => {
      if (!titleOrType) {
        navigate('/games/memory');
        return;
      }

      try {
        // Сначала проверяем, является ли это типом игры (для обратной совместимости)
        const normalizedType = titleOrType.toLowerCase();
        if (constructorComponents[normalizedType]) {
          console.log('📍 Direct type match:', normalizedType);
          setConstructorComponent(() => constructorComponents[normalizedType]);
          setResolvedGame({ title: titleOrType, gameType: normalizedType });
          setLoading(false);
          return;
        }

        // Если не тип, пытаемся загрузить игру по названию
        console.log('📍 Fetching game by title:', titleOrType);
        // Убираем /api, так как baseURL axios скорее всего уже настроен на /api 
        // или прокси перенаправляет /api.
        // Но если это голый axios, то baseURL не задан?
        // Судя по логам http://localhost:3003/api/api/..., baseURL=/api
        const response = await axios.get(`/public/games/${encodeURIComponent(titleOrType)}?t=${Date.now()}`);
        const game = response.data;

        if (!game || !game.gameType) {
          setError(`Game "${titleOrType}" not found or has no gameType`);
          setLoading(false);
          return;
        }

        console.log('📍 Game found:', { title: game.title, gameType: game.gameType });

        // Определяем компонент конструктора по gameType
        const gameType = game.gameType.toLowerCase();
        const component = constructorComponents[gameType];

        if (!component) {
          setError(`Constructor for game type "${game.gameType}" not found`);
          setLoading(false);
          return;
        }

        // Сохраняем дизайн в localStorage для совместимости
        try {
          const designId = game.name || game.title;
          localStorage.setItem(`selectedDesign_${designId}`, JSON.stringify(game));
        } catch (e) {
          console.warn('Failed to save design to localStorage', e);
        }

        setConstructorComponent(() => component);
        setResolvedGame(game);

        // Если это paywall режим, проверяем и обновляем title в localStorage
        if (art) {
          const safeGameName = (game.title || 'Memory').replace(/[^a-zA-Z0-9]/g, '_');
          const storageKey = `temp_paywall_${art}_${safeGameName}${storefrontSuffix}`;
          const stored = localStorage.getItem(storageKey);
          if (stored) {
            try {
              const parsed = JSON.parse(stored);
              // Если title изменился, очищаем старые данные и создаем новые
              if (parsed.title && parsed.title !== game.title) {
                console.log('Title changed, clearing old paywall data for art:', art, 'old title:', parsed.title, 'new title:', game.title);
                const newKey = secureUuid().replace(/-/g, '').substring(0, 32);
                const newBuildId = secureUuid();
                localStorage.setItem(storageKey, JSON.stringify({
                  key: newKey,
                  buildId: newBuildId,
                  isCreated: false,
                  title: game.title
                }));
              } else {
                // Пытаемся сохранить и старый ключ для обратной совместимости во время перехода
                localStorage.setItem(`temp_paywall_${art}${storefrontSuffix}`, JSON.stringify({
                  ...parsed,
                  title: game.title
                }));
                
                // Обновляем основной ключ
                localStorage.setItem(storageKey, JSON.stringify({
                  ...parsed,
                  title: game.title
                }));
              }
            } catch (e) {
              console.warn('Failed to update title in localStorage', e);
            }
          } else {
            // Если данных нет, создаем новые
            const newKey = secureUuid().replace(/-/g, '').substring(0, 32);
            const newBuildId = secureUuid();
            localStorage.setItem(storageKey, JSON.stringify({
              key: newKey,
              buildId: newBuildId,
              isCreated: false,
              title: game.title
            }));
          }
        }

        setLoading(false);
      } catch (err) {
        console.error('Error loading constructor:', err);
        setError(`Failed to load game "${titleOrType}"`);
        setLoading(false);
      }
    };

    loadConstructor();
  }, [titleOrType, navigate, constructorComponents, art]);

  if (loading) {
    return (
      <div style={{ 
        height: '100vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        fontFamily: 'sans-serif', 
        color: '#666' 
      }}>
        Loading constructor...
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', fontFamily: 'sans-serif' }}>
        <h3 style={{ color: '#d32f2f' }}>Error</h3>
        <p>{error}</p>
        <button 
          onClick={() => navigate('/games/memory')}
          style={{
            marginTop: 20,
            padding: '10px 20px',
            background: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: 4,
            cursor: 'pointer'
          }}
        >
          Go to Games Catalog
        </button>
      </div>
    );
  }

  if (!ConstructorComponent || !resolvedGame) {
    return null;
  }

  // --- Logic for GameKey generation ---
  if (art && (!authData?.gameKey || storefrontPrefill)) {
    // Если есть артикул (товар WooCommerce), но нет авторизации ключа, создаем временный объект игры

    // Пытаемся получить сохраненный title и isPaid из localStorage
    let savedTitle = resolvedGame.title;
    let finalIsCreated = isCreated || actuallyCreated || false;
    let finalIsPaid = syncedIsPaid ?? storedIsPaid ?? false;
    
    try {
      const safeGameName = (titleOrType || 'Memory').replace(/[^a-zA-Z0-9]/g, '_');
      const storageKey = `temp_paywall_${art}_${safeGameName}${storefrontSuffix}`;
      let stored = localStorage.getItem(storageKey);
      
      // Fallback на старый ключ
      if (!stored) {
        stored = localStorage.getItem(`temp_paywall_${art}${storefrontSuffix}`);
      }

      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.title) {
          savedTitle = parsed.title;
        }
        if (parsed.isCreated) {
          finalIsCreated = true;
        }
        if (parsed.isPaid === true) {
          finalIsPaid = true;
        } else if (parsed.isPaid === false && syncedIsPaid === null) {
          finalIsPaid = false;
        }
      }
    } catch (e) {
      console.warn('Failed to read title from localStorage', e);
    }

    // С ?key= не отдаём конструктор, пока не резолвили buildId — иначе checkout уйдёт без game_id
    if (urlKey && (!keyResolveDone || !reservedBuildId)) {
      return <div>Loading...</div>;
    }

    const tempGameKey: GameKey = {
      _id: 'temp',
      gameBuildId: reservedBuildId,
      key: reservedKey, // Резервный ключ для отображения ссылки
      // Важно: берем название игры из resolvedGame (это title из БД),
      // чтобы конструктор знал, какую тему загружать
      gameName: savedTitle || titleOrType || '',
      gameType: resolvedGame.gameType,
      isPaid: finalIsPaid,
      articleId: art, // ID товара WooCommerce
      price: price ? parseFloat(price) : 0,
      gameDayCount: 30,
      editDayCount: 7,
      isCreated: finalIsCreated, // Используем актуальное значение
    } as any;

    return <ConstructorComponent gameKey={tempGameKey} />;
  }

  // Для прямого доступа к конструктору (не paywall режим) создаем временный gameKey с данными игры
  if (!authData?.gameKey && resolvedGame) {
    // Очищаем localStorage от старых данных для разных игр
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('temp_paywall_')) {
        try {
          const stored = localStorage.getItem(key);
          if (stored) {
            const parsed = JSON.parse(stored);
            if (parsed.title !== resolvedGame.title) {
              console.log('Clearing old paywall data for different game:', key, 'title:', parsed.title, 'new title:', resolvedGame.title);
              localStorage.removeItem(key);
            }
          }
        } catch (e) {
          console.warn('Failed to check localStorage key:', key, e);
        }
      }
    });

    const tempGameKey: GameKey = {
      _id: 'temp',
      gameBuildId: '',
      key: '', // Пустой ключ для нового создания
      gameName: resolvedGame.title,
      gameType: resolvedGame.gameType,
      isPaid: true, // Для прямого доступа считаем оплаченным
      gameDayCount: 30,
      editDayCount: 7,
      isCreated: false,
    } as any;

    return <ConstructorComponent gameKey={tempGameKey} />;
  }

  // Если пользователь уже авторизован (редактирует существующую игру)
  return <ConstructorComponent gameKey={authData?.gameKey || null} />;
};
